import { useContext, useEffect } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { BrowserRouter as Router, Route, Switch, NavLink, Redirect } from 'react-router-dom';
import Perfil from 'src/containers/guestDashboard/Profile';
import Grupos from 'src/containers/guestDashboard/Groups';
import Crecer from 'src/containers/guestDashboard/Crecer';
import Eventos from 'src/containers/guestDashboard/Events'; // Importa el componente Eventos
import Encuestas from 'src/containers/guestDashboard/Encuestas'; // Importa el componente Encuestas
import Experiencia from 'src/containers/guestDashboard/Experiencia'; // Importa el componente Experiencia
import { DashboardComponent } from 'src/containers/guestDashboard/styles/DashboardStyles';
import { screenSizes } from 'src/assets/styles/Literals';
import {
  GuestDashboardContext,
  GuestDashboardProvider,
} from 'src/containers/guestDashboard/guestDashboardContext';
import {
  getAVolunteer,
  getCrecerDates,
  getGuest,
  getGuestGroup,
  getGuestGroups,
} from 'src/containers/guestDashboard/guestDashboardApiCalls';
import GuestLogin from 'src/containers/guestDashboard/guestLogin';
import queryString from 'query-string';
import { changeDocTitle } from 'src/components/helpers';
import {
  isCaptchaRequired,
  useRecaptchaToken,
  RECAPTCHA_SECURITY_ERROR,
} from 'src/components/helpers/recaptchaUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHome,
  faUsers,
  faSeedling,
  faCalendar,
  faPoll,
  faStar,
} from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';

/** Fills the dashboard shell; keeps flex min-height chain for nested scroll. */
const DashboardShell = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

/** Main row: sidebar + content; bottom nav uses position:fixed and stays out of flex flow on tablet. */
const DashboardMainRow = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`;

/**
 * Routed content: horizontal inset on small tablets / narrow widths,
 * bottom inset when the bottom nav is visible so content is not covered.
 */
const DashboardContent = styled.div`
  flex: 1;
  min-height: 0;
  min-width: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;

  @media (max-width: ${screenSizes.laptop}) {
    padding-left: clamp(8px, 2vw, 20px);
    padding-right: clamp(8px, 2vw, 20px);
  }

  @media (max-width: ${screenSizes.tablet}) {
    padding-left: max(8px, env(safe-area-inset-left, 0px));
    padding-right: max(8px, env(safe-area-inset-right, 0px));
    ${(p) =>
      p.$session &&
      css`
        /* Bottom bar is 60px in DashboardStyles; extra gap for scroll end + home indicator */
        padding-bottom: calc(64px + env(safe-area-inset-bottom, 0px));
      `}
  }
`;

const GuestDashboard = () => {
  const iconMapping = {
    perfil: faHome,
    grupos: faUsers,
    crecer: faSeedling,
    eventos: faCalendar,
    encuestas: faPoll,
    experiencia: faStar,
  };

  const {
    guest,
    setGuest,
    loginCredentials,
    setCrecerDates,
    setExperienceVolunteer,
    setLoginCredentials,
    setGuestGroup,
    setGuestGroups,
  } = useContext(GuestDashboardContext);

  const getGroupCaptchaToken = useRecaptchaToken('group_login');
  const getAllGroupsCaptchaToken = useRecaptchaToken('all_couple_groups');

  let query = window.location.search;
  let parsedQueryString = queryString.parse(query);

  useEffect(() => {
    if (parsedQueryString.identification && parsedQueryString.phone) {
      getInfo(parsedQueryString);
    }
  }, []);

  const getInfo = async (inputs) => {
    const found = await getGuest({ ...(inputs || {}), setGuest });
    if (found) {
      setLoginCredentials(inputs);
    }
  };

  useEffect(() => {
    changeDocTitle('Perfil');
    if (loginCredentials?.identification && loginCredentials?.phone) {
      getAVolunteer({ ...loginCredentials, setExperienceVolunteer });

      (async () => {
        const captchaRequired = isCaptchaRequired();
        const [groupCaptcha, allGroupsCaptcha] = await Promise.all([
          getGroupCaptchaToken({ skip: !captchaRequired }),
          getAllGroupsCaptchaToken({ skip: !captchaRequired }),
        ]);
        if (captchaRequired && (!groupCaptcha || !allGroupsCaptcha)) {
          Swal.fire(RECAPTCHA_SECURITY_ERROR);
          return;
        }
        getGuestGroups({
          ...loginCredentials,
          ...(captchaRequired ? { captcha: allGroupsCaptcha } : {}),
          setGuestGroups,
        });
        getGuestGroup({
          ...loginCredentials,
          ...(captchaRequired ? { captcha: groupCaptcha } : {}),
          setGuestGroup,
        });
      })();
    }
    getCrecerDates({ setCrecerDates });
  }, [loginCredentials]);

  return (
    <DashboardComponent>
      <DashboardShell>
        <Router>
          <DashboardMainRow>
            {!!guest?.id ? (
              <>
                <div className="sidebar">
                  <div className="containerImg">
                    <img src={guest?.photo} alt="" className="profile-img" />
                    <h4
                      style={{
                        marginTop: '10px',
                        marginBottom: '0px',
                        fontWeight: 400,
                        fontSize: '23px',
                      }}
                    >
                      {guest?.name}
                    </h4>
                    <h4 style={{ marginTop: '2px', fontWeight: 200, fontSize: '16px' }}>
                      {guest?.status}
                    </h4>
                  </div>
                  <br></br>
                  <ul className="menuList">
                    <li>
                      <NavLink exact to="/perfil/info" activeClassName="activePerfil">
                        <span className="icon">
                          <FontAwesomeIcon icon={iconMapping.perfil} />
                        </span>
                        <span className="text">Perfil</span>
                      </NavLink>
                    </li>
                    <li>
                      <NavLink exact to="/perfil/crecer" activeClassName="activeCrecer">
                        <span className="icon">
                          <FontAwesomeIcon icon={iconMapping.crecer} />
                        </span>
                        <span className="text">Crecer</span>
                      </NavLink>
                    </li>
                    <li>
                      <NavLink exact to="/perfil/grupos" activeClassName="activeGrupos">
                        <span className="icon">
                          <FontAwesomeIcon icon={iconMapping.grupos} />
                        </span>
                        <span className="text">Grupos</span>
                      </NavLink>
                    </li>
                    <li>
                      <NavLink exact to="/perfil/experiencia" activeClassName="activeExperiencia">
                        <span className="icon">
                          <FontAwesomeIcon icon={iconMapping.experiencia} />
                        </span>
                        <span className="text">Experiencia</span>
                      </NavLink>
                    </li>
                  </ul>
                </div>
                <DashboardContent $session>
                  <Switch>
                    <Route path="/perfil/info" component={Perfil} />
                    <Route path="/perfil/grupos" component={Grupos} />
                    <Route path="/perfil/crecer" component={Crecer} />
                    <Route path="/perfil/eventos" component={Eventos} />
                    <Route path="/perfil/encuestas" component={Encuestas} />
                    <Route path="/perfil/experiencia" component={Experiencia} />
                    <Redirect exact from="/perfil" to="/perfil/info" />
                  </Switch>
                </DashboardContent>
              </>
            ) : (
              <DashboardContent>
                <GuestLogin getInfo={getInfo} />
              </DashboardContent>
            )}
          </DashboardMainRow>
        </Router>
      </DashboardShell>
    </DashboardComponent>
  );
};

const DashboardWithProvider = () => (
  <GuestDashboardProvider>
    <GuestDashboard />
  </GuestDashboardProvider>
);

export default DashboardWithProvider;
